import { memo, useEffect, useState } from "react"
import { motion } from "motion/react"
import { IconXmarkOutline18 as X } from "nucleo-ui-outline-18"
import { useMedia } from "react-use"

import type { CopilotScope } from "@productlane/lib/src/ai"
import { copilotScopeKey } from "@productlane/lib/src/ai"
import { cn } from "@productlane/lib/src/cn"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import { IconButton, Shortcut, Tooltip } from "@productlane/ui"

import type { CopilotPageContext } from "./sidebar-ai-chat-sync"
import {
	COPILOT_SHEET_QUERY,
	useCopilotState,
	useIsCopilotOpen,
} from "@/lib/states/copilot-state"
import { useRole } from "@/lib/useRole"
import { useReleaseCopilotFocus } from "./copilot-focus-release"
import { ExpandToAgentButton } from "./expand-to-agent-button"
import { SidebarAiChat } from "./sidebar-ai-chat"
import { useSidebarAiChatState } from "./sidebar-ai-chat-state"

export const COPILOT_PANEL_WIDTH = "22rem"

// Standalone copilot panel for the docs, changelog, and inbox surfaces. Visibility
// is driven by `useCopilotState` matching this panel's scope, so a copilot
// left open on one surface never renders on another.
export const CopilotOverlay = memo(function CopilotOverlay({
	scope,
	getPageContext,
	contextTitle,
	contextIcon,
	className,
	layout = "overlay",
}: {
	scope: CopilotScope
	getPageContext?: () => CopilotPageContext | undefined
	contextTitle?: string
	contextIcon?: string | null
	className?: string
	layout?: "overlay" | "push"
}) {
	const isOpen = useIsCopilotOpen(scope)
	const isSheet = useMedia(COPILOT_SHEET_QUERY, false)
	const seedMessage = useCopilotState((s) => s.seedMessage)
	const close = useCopilotState((s) => s.close)
	const consumeSeed = useCopilotState((s) => s.consumeSeed)
	const { role } = useRole()
	const canSeeAiChat = role !== "VIEWER"

	const scopeKey = copilotScopeKey(scope)
	const isVisible = isOpen && canSeeAiChat

	const [hasMounted, setHasMounted] = useState(isVisible)
	useEffect(() => {
		if (isVisible) setHasMounted(true)
	}, [isVisible])

	const panelRef = useReleaseCopilotFocus(isVisible)

	// Seed the first message once the chat is actually mounted for this scope.
	// Gating on hasMounted matters: the child sync runs resetForScope (which
	// clears any pending prompt) on mount, and child effects fire before this
	// parent effect, so setting the prompt here survives the reset and drains
	// into a real message. Seeding before the sync mounts would be wiped.
	useEffect(() => {
		if (!isVisible || !hasMounted || !seedMessage) return
		useSidebarAiChatState.getState().setPendingPrompt(seedMessage)
		consumeSeed()
	}, [isVisible, hasMounted, seedMessage, scopeKey, consumeSeed])

	if (!canSeeAiChat) return null

	const panelBody = hasMounted && (
		<SidebarAiChat
			scope={scope}
			getPageContext={getPageContext}
			contextTitle={contextTitle}
			contextIcon={contextIcon}
		/>
	)

	const closeButton = (
		<div className="absolute right-3 top-3.5 z-30 flex items-center gap-1">
			<ExpandToAgentButton scope={scope} onExpand={() => close(scope)} />
			<Tooltip
				label={
					<span className="inline-flex items-center gap-2">
						Close
						<Shortcut>
							{ShortcutSymbols.CMD}
							{ShortcutSymbols.J}
						</Shortcut>
					</span>
				}
				side="bottom"
			>
				<IconButton
					Icon={X}
					iconStrokeWidth={1.5}
					variant="ghost"
					size="default"
					aria-label="Close AI Assistant"
					onClick={() => close(scope)}
				/>
			</Tooltip>
		</div>
	)

	// A phone has no room beside the editor, so the copilot covers the app. The
	// key stops motion carrying the side panel's inline width onto the sheet.
	if (isSheet) {
		return (
			<motion.div
				key="copilot-sheet"
				ref={panelRef}
				aria-hidden={!isVisible}
				initial={false}
				animate={{ x: isVisible ? "0%" : "100%" }}
				transition={{ duration: 0.24, ease: [0.23, 1, 0.32, 1] }}
				className={cn(
					"fixed inset-0 z-50 flex flex-col overflow-hidden bg-gray-50 will-change-transform dark:bg-gray-850",
					!isVisible && "pointer-events-none",
					className,
				)}
			>
				{closeButton}
				{panelBody}
			</motion.div>
		)
	}

	if (layout === "push") {
		return (
			<motion.div
				key="copilot-push"
				ref={panelRef}
				aria-hidden={!isVisible}
				initial={false}
				animate={{
					width: isVisible ? COPILOT_PANEL_WIDTH : 0,
					opacity: isVisible ? 1 : 0,
				}}
				transition={{ duration: 0.24, ease: [0.23, 1, 0.32, 1] }}
				className={cn(
					"relative flex h-full shrink-0 flex-col overflow-hidden bg-gray-50 will-change-[width] dark:bg-gray-850",
					isVisible && "border-l border-gray-200 dark:border-gray-700/80",
					!isVisible && "pointer-events-none",
					className,
				)}
			>
				<div className="flex h-full w-[22rem] flex-col overflow-hidden">
					{closeButton}
					{panelBody}
				</div>
			</motion.div>
		)
	}

	return (
		<motion.div
			key="copilot-overlay"
			ref={panelRef}
			aria-hidden={!isVisible}
			initial={false}
			animate={{
				x: isVisible ? 0 : 24,
				opacity: isVisible ? 1 : 0,
			}}
			transition={{ duration: 0.24, ease: [0.23, 1, 0.32, 1] }}
			className={cn(
				"absolute right-0 top-0 z-30 flex h-full flex-col overflow-hidden border-l border-gray-200 bg-gray-50 will-change-transform dark:border-gray-700/80 dark:bg-gray-850",
				!isVisible && "pointer-events-none",
				className,
			)}
			style={{ width: COPILOT_PANEL_WIDTH }}
		>
			{closeButton}
			{panelBody}
		</motion.div>
	)
})
